import * as Sentry from '@sentry/react';
import { AlertCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Spinner } from './ui/spinner';

type RecordListScrollProps = {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isFetchingNextPage: boolean;
  bottomSentinelRef: (node?: Element | null) => void;
  children: React.ReactNode;
};

/**
 * Shared loading/error/infinite-scroll chrome for the record list views. Renders a spinner while
 * the first page loads, an error alert on failure, and otherwise the list plus a bottom sentinel
 * (paired with {@link useInfiniteRecordList}) and a spinner while the next page is fetched. The
 * empty state is owned by the list component itself (each renders its own "no records" message).
 */
export const RecordListScroll = ({
  isLoading,
  isError,
  error,
  isFetchingNextPage,
  bottomSentinelRef,
  children,
}: RecordListScrollProps): React.ReactElement => {
  const { t } = useTranslation('records');

  // Show a localized message to the user; report the raw error to Sentry for debugging. tRPC
  // serializes server-side error text into the client error, which we don't want to render verbatim.
  useEffect(() => {
    if (isError && error) Sentry.captureException(error, { tags: { component: 'RecordListScroll' } });
  }, [isError, error]);

  if (isLoading) {
    return (
      <div role="status" aria-label={t('loadingRecords')} className="flex items-center justify-center p-8">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertTitle>{t('errorLoadingRecords')}</AlertTitle>
        <AlertDescription>{t('failedToLoadRecords')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      {children}
      <div ref={bottomSentinelRef} aria-hidden="true" className="h-px" />
      {isFetchingNextPage && (
        <div role="status" aria-label={t('loadingMoreRecords')} className="flex items-center justify-center p-4">
          <Spinner className="size-5" />
        </div>
      )}
    </>
  );
};
